<template>
  <div class="modifier-set">
    <h5>
      {{ modifierSet.name }}
      <transition name="requirement-status">
        <span
          v-if="required"
          :class="{ required, met: isRequirementMet, 'animate__animated': true, 'animate__bounceIn': isRequirementMet }"
        >
          <SvgIcon name="check-circle" v-if="isRequirementMet"></SvgIcon>
          <SvgIcon name="warning" v-else></SvgIcon>
          <span v-if="!isRequirementMet">{{ $t('required') }}</span>
        </span>
      </transition>
    </h5>
    <p>{{ modifierSet.description }}</p>
    <p v-safe-html="instructions"></p>
    <div class="row">
      <div
        v-for="(modifier, index) in modifierSet.modifiers"
        :key="modifier.ref"
        :class="{'col-md-6': true, 'full-width': isLastOdd(index)}"
      >
        <NestedModifier 
          :parentModifierSetRef="parentModifierSetRef" 
          :parentModifierRef="parentModifierRef" 
          :modSetRef="modifierSet.ref"
          :modifier="modifier" 
          :isSelected="isSelected(modifier)" 
          :modifierType="modType"
          :disabled="isMaxSelected && !isSelected(modifier)" 
          :selectedGraph="selectedGraphForModifier(modifier.ref)" 
        />
      </div>
    </div>
  </div>
</template>

<script>
import NestedModifier from "./NestedModifier.vue";

export default {
  name: "NestedModifierSet",
  props: {
    modifierSet: Object,
    parentModifierSetRef: String,
    parentModifierRef: String,
    selectedGraph: Array,
  },
  components: {
    NestedModifier,
  },
  methods: {
    isSelected(modifier) {
      return this.selectedGraph.some((m) => m.modifierRef === modifier.ref);
    },
    selectedGraphForModifier(ref) {
      const m = this.selectedGraph.find((m) => m.modifierRef === ref);
      return m ? m.lineModifierSets : [];
    },
    isLastOdd(index) {
      return this.modifierSet.modifiers.length % 2 !== 0 && index === this.modifierSet.modifiers.length - 1;
    },
  },
  computed: {
    modType() {
      const { max } = this.modifierSet;
      return max === 1 ? "radio" : "checkbox";
    },
    required() {
      return this.modifierSet.min > 0;
    },
    isMaxSelected() {
      return this.modType !== "radio" && this.selectedGraph.length >= this.modifierSet.max;
    },
    instructions() {
      const { min, max } = this.modifierSet;
      const totalModifiers = this.modifierSet?.modifiers?.length || 0;
      if (min === 1 && max === 1) {
        return this.$t("chooseOne");
      } else if (min === 0 && max === 1) {
        return this.$t("optional");
      } else if (min > 0 && max === undefined) {
        return `${this.$t('chooseAtLeast')} <strong>${min}</strong>`;
      } else if (min > 0 && max > min) {
        return `${this.$t('chooseBetween')} <strong>${min}</strong> - <strong>${totalModifiers}</strong>`;
      } else if (!min && max > 0 && max !== this.modifierSet.modifiers.length) {
        const maxString = max > this.modifierSet.modifiers.length ? this.modifierSet.modifiers.length : max;
        return `${this.$t('chooseUpTo')} <strong>${maxString}</strong>`;
      } else if (min > 0 && max === min) {
        return `${this.$t('choose')} <strong>${min}</strong>`;
      } else {
        return '';
      }
    },
    isRequirementMet() {
      const selectedModifiers = this.selectedGraph;
      const min = this.modifierSet.min || 0;
      const max =
        this.modifierSet.max === undefined
          ? selectedModifiers.length
          : this.modifierSet.max;
      return selectedModifiers.length >= min && selectedModifiers.length <= max;
    },
  },
};
</script>

<style scoped>
.modifier-set .row {
  display: flex;
  flex-wrap: wrap;
  margin: -0.5rem; /* Negative margin to offset the padding */
}
.modifier-set .col-md-6 {
  display: flex;
  flex-direction: column;
  padding: 0.5rem; /* Add padding for equal spacing */
  margin: 0; /* Ensure no default margin is applied */
}
.modifier-set .col-md-6.full-width {
  flex: 0 0 100%;
}
.modifier-set .col-md-6 > * {
  flex: 1;
}

.requirement-status-enter-active,
.requirement-status-leave-active {
  transition: opacity 0.5s;
}
.requirement-status-enter, .requirement-status-leave-to {
  opacity: 0;
}
</style>
